import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";
import ContactForm from "@/components/site/ContactForm";

const ACCENT = "#F57F17"; // gold

const QUESTIONS = [
  {
    q: "Today's teenagers can navigate any app instantly — but would they know what to do in the thirty seconds after money leaves their account to a fraud?",
    f: "Being good with phones is not the same as being safe with them. The two are unrelated skills.",
  },
  {
    q: "A generation that can find any fact online — but do they know which questions even exist to ask?",
    f: "You cannot search for a danger or a right you've never heard of. AI answers questions; it cannot tell you a question exists.",
  },
  {
    q: "Children who can score brilliantly in maths — but can most explain how compound interest could make them wealthy, or how a loan could quietly trap them?",
    f: "The maths is identical. One version is on the exam. The version that decides their financial life is not.",
  },
  {
    q: "A generation more connected than any before it — but are they being taught how to pause before reacting in anger, or how relationships actually survive conflict?",
    f: "These are learnable, evidence-based skills — taught to almost no one, and quietly deciding the quality of their adult lives.",
  },
];

const LEVEL_CARDS = [
  { accent: "#004D40", t: "At 10–12, a child who can handle the home.", d: "Stays calm in a small crisis, knows what to do, becomes a help in an emergency instead of one more worry." },
  { accent: "#004D40", t: "At 12–14, a teenager who can't be cheated.", d: "Sees through a scam, knows their rights, handles a bank or an office without being sent to a middleman." },
  { accent: "#F57F17", t: "At 14–16, a young adult who leads their life.", d: "Manages emotions under pressure, understands money from the start, and chooses a path around what they actually value." },
];

export default function ForParents() {
  useReveal();
  useEffect(() => { document.title = "For Parents — LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#0D1B2A] text-white relative">
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>For Parents</div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[66px] leading-[1.05] tracking-tight">
              You taught them to study. Who teaches them to live?
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              School measures what a child knows for an exam. LifeIQ builds what a child can actually do when life happens — handle a home emergency, see through a scam, understand money, and grow into a capable, grounded adult.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>A gentle question to sit with</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Not about any one child — about an entire generation growing up brilliant on paper.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">Notice the ones that give you pause.</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              If any of these gave you pause — that is the gap. It is no one's fault. It simply sits outside what school was ever built to do. LifeIQ exists to fill it.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOUR CHILD BECOMES */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What changes</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              What changes — quietly, then completely.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {LEVEL_CARDS.map((c, i) => (
              <article key={i} data-testid={`parent-level-${i}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm overflow-hidden flex flex-col" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="h-1.5" style={{ backgroundColor: c.accent }} />
                <div className="p-7 sm:p-8 flex flex-col gap-4 grow">
                  <h3 className="font-serif-d text-[22px] sm:text-[24px] leading-[1.25] text-[#0D1B2A]">{c.t}</h3>
                  <p className="text-[16px] text-[#1A1A1A] leading-[1.7]">{c.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* IT COMES HOME */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>It doesn't stay at school</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              And it doesn't stay at school.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Every LifeIQ chapter sends learning home. A child who learns the rules of fraud often becomes the one who protects a parent or grandparent from a scam call. A child who learns about money starts real conversations at the dinner table. The protection and the capability flow outward — from the student, to the whole family.
          </p>
          <p className="mt-10 font-serif-d italic text-[26px] sm:text-[30px] leading-[1.3] reveal" style={{ color: ACCENT }}>
            "Many parents say the same thing: my child taught me something I didn't know."
          </p>
        </div>
      </section>

      {/* WHY NOT JUST LEAVE IT TO AI */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>A fair question</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              But they have AI for all this — don't they?
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            AI is a remarkable tool — for someone who already knows what to ask. It cannot warn a child about a danger they've never heard of, and it cannot act in the three seconds before a scam succeeds. LifeIQ builds the awareness and the trained reflex first. Then AI makes a capable young person even sharper. The two work together — but the reflex has to come first, and only practice builds it.
          </p>
        </div>
      </section>

      {/* WHAT YOU CAN DO */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">The most powerful thing a parent can do</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-white">
              The school is the doorway.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              LifeIQ is delivered through schools — which means the single most effective step you can take is to put it in front of the people who run yours. A recommendation from a parent carries real weight with a school's leadership.
            </p>
            <a href="#recommend" data-testid="parents-recommend" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              Recommend LifeIQ to a School <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FORM */}
      <section id="recommend" className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 reveal">
              <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Help bring LifeIQ in</div>
              <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[48px] leading-[1.08] text-[#0D1B2A]">
                Help bring LifeIQ to your child's school.
              </h2>
              <p className="mt-7 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
                Share your details and your school's name. We'll reach out respectfully — and we're happy to send you the full curriculum overview to pass along.
              </p>
            </div>
            <div className="md:col-span-7 reveal">
              <ContactForm
                idPrefix="parents-contact"
                theme="light"
                showRole={false}
                schoolLabel="Your child's school name"
                schoolPlaceholder="School name"
                submitLabel="Send"
                source="for-parents"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="hidden">
        <Link to="/contact" />
      </div>
    </main>
  );
}
